const Reservation = require('@/lib/Reservations/Reservation');
const { hydrate } = require('@/lib/Hydration');

class ReservationService {
    /**
     * @param {ApiClient} apiClient
     * @param {CartToReservationStructureMapper} cartToReservationStructureMapper
     */
    constructor(
        apiClient,
        cartToReservationStructureMapper
    ) {
        this.apiClient = apiClient;
        this.cartToReservationStructureMapper = cartToReservationStructureMapper;
    }

    /**
     * @param {string} reservationToken
     *
     * @return {Promise<boolean>}
     */
    async deleteReservation(reservationToken) {
        return this.apiClient
            .request(
                'DELETE',
                `reservations/${reservationToken}`
            )
            .then(() => {
                return true;
            })
            .catch((err) => {
                console.warn(
                    'Failed to delete reservation with token:',
                    reservationToken,
                    err
                );

                throw err;
            });
    }

    /**
     * @param {Cart} cart
     *
     * @return {Promise<Reservation>}
     */
    async putReservationForCart(cart) {
        const reservationStructure = this.convertCartToReservationStructure(cart);

        return this.putReservation(reservationStructure);
    }

    /**
     * This will call the API endpoint to PUT a reservation with a given thirdPartyRef.
     * If a reservation with that ref already exists it will be replaced.
     * If not reservation exists it will be created.
     * That makes it idempotent, and it's safe to call this multiple times. It doesn't matter if the previous
     * reservation has already expired - this will recreate it (if still allowed).
     *
     * @param {ReservationStructure} reservationStructure
     *
     * @returns {Promise<Reservation>}
     */
    async putReservation(reservationStructure) {
        return this.apiClient
            .request(
                'PUT',
                'reservations/by-third-party-ref',
                reservationStructure
            )
            .then((response) => {
                if (!response.data?.reservation) {
                    console.error(response);
                    throw new Error('Failed to create reservation.');
                }

                const reservation = new Reservation();
                hydrate(response.data.reservation, reservation);

                return reservation;
            })
            .catch((err) => {
                console.warn(
                    'Failed to create reservation.',
                    err,
                );

                throw err;
            });
    }

    /**
     * @param {string} thirdPartyRef
     *
     * @returns {Promise<Reservation|null>}
     */
    async getReservationByThirdPartyRef(thirdPartyRef) {
        return this.apiClient
            .request(
                'GET',
                'reservations/by-third-party-ref',
                {
                    thirdPartyRef,
                }
            )
            .then((response) => {
                if (!response.data?.reservation) {
                    return null;
                }

                const reservation = new Reservation();
                hydrate(response.data.reservation, reservation);

                return reservation;
            })
            .catch(() => {
                return null;
            });
    }

    /**
     * @param {Cart} cart
     *
     * @return {ReservationStructure}
     */
    convertCartToReservationStructure(cart) {
        return this.cartToReservationStructureMapper.removeZeroQtyLines(
            this.cartToReservationStructureMapper.map(cart)
        );
    }
}

module.exports = ReservationService;
